import { InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { Field, FieldProps } from 'formik';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { FieldConfigProps } from '.';
import { getCurrencySymbol, getUuid } from '../../utils';
import { get } from 'lodash';
import { Currency } from '../../constants';

export type FormTextFieldProps = FieldConfigProps &
    Partial<Omit<TextFieldProps, 'onChange'>> & {
        allCaps?: boolean;
        onChange?: (value: string) => void;
    };

export const FormTextField: React.FunctionComponent<FormTextFieldProps> = ({
    name,
    validate,
    variant,
    allCaps,
    value,
    ...rest
}) => {
    /*
       HACK: due to Chrome's limitation, `formik` doesn't know about autofill data
       until the user focuses on the app. This causes overlapping between the label
       and the autofill data. This is a workaround until we have a proper solution.
       https://github.com/facebook/react/issues/1159#issuecomment-1025423604
    */
    const id = useMemo(() => getUuid(), []);
    const [wasInitiallyAutofilled, setWasInitiallyAutofilled] = useState(false);
    useLayoutEffect(() => {
        const checkAutofilled = () => {
            try {
                const autofilled = !!document.getElementById(id)?.matches('*:autofill');
                setWasInitiallyAutofilled(autofilled);
            } catch (error) {
                // no-op
            }
        };

        for (let i = 1; i <= 10; i++) {
            setTimeout(checkAutofilled, i * 100);
        }
    }, [id]);

    return (
        <Field name={name} validate={validate}>
            {({ field, form: { touched, errors, setFieldValue } }: FieldProps) => {
                // FIXME: can't use meta data due to https://github.com/jaredpalmer/formik/issues/343
                const error = get(touched, field.name) && (get(errors, field.name) as string);

                return (
                    <TextField
                        fullWidth
                        variant={variant ?? 'outlined'}
                        {...field}
                        {...rest}
                        id={id}
                        InputLabelProps={{
                            ...rest.InputLabelProps,
                            shrink: wasInitiallyAutofilled || rest.InputLabelProps?.shrink,
                        }}
                        onChange={(event) => {
                            let value: string | number | undefined = allCaps
                                ? event.target.value.toUpperCase()
                                : event.target.value;

                            if (rest.type === 'number') {
                                value = value !== '' ? Number(value) : undefined;
                            }

                            setFieldValue(name, value);
                            rest.onChange?.(value as string);

                            // Since the field is changed manually, this flag is no longer needed
                            setWasInitiallyAutofilled(false);
                        }}
                        error={!!error}
                        helperText={error ?? rest.helperText}
                        value={field.value ?? value ?? ''}
                    />
                );
            }}
        </Field>
    );
};

export type FormNumberFieldProps = Omit<FormTextFieldProps, 'onChange'> & {
    min?: number;
    max?: number;
    step?: string;
    onChange?: (value: number | null) => void;
    currency?: string;
};

export const FormNumberField: React.FunctionComponent<FormNumberFieldProps> = ({
    name,
    validate,
    min,
    max,
    step,
    onChange,
    ...rest
}) => {
    return (
        <FormTextField
            name={name}
            validate={validate}
            onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()} // prevent mousewheel to change value of input
            {...rest}
            // @ts-expect-error: value is already converted in FormTextField
            onChange={onChange}
            inputProps={{
                min,
                max,
                step,
                ...rest.inputProps,
            }}
            type="number"
        />
    );
};

export const FormMoneyField: React.FunctionComponent<FormNumberFieldProps> = ({
    name,
    validate,
    currency = Currency.UsDollar,
    ...rest
}) => {
    return (
        <FormNumberField
            name={name}
            validate={validate}
            {...rest}
            InputProps={{
                startAdornment: <InputAdornment position="start">{getCurrencySymbol(currency)}</InputAdornment>,
                ...rest.InputProps,
            }}
        />
    );
};

const percentageSymbol = <InputAdornment position="start">%</InputAdornment>;
export const FormPercentageField: React.FunctionComponent<FormNumberFieldProps> = ({ name, validate, ...rest }) => {
    return (
        <FormNumberField
            name={name}
            validate={validate}
            {...rest}
            InputProps={{
                endAdornment: percentageSymbol,
                ...rest.InputProps,
            }}
        />
    );
};
